import { ExternalLinkIcon, FileIcon, HandshakeIcon, PresentationIcon, ShapesIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'

import { MarketplaceCard } from 'slab'

type ResourceCategory = 'All' | 'Assets' | 'Education'

interface Resource {
  id: string
  name: string
  description: string
  category: string
  url: string
  image_url: string
  type: 'document' | 'url' | 'presentation'
}

const ResourceTypeTag = ({ type }: { type: Resource['type'] }) => {
  const config = {
    document: {
      label: 'Document',
      icon: FileIcon,
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700'
    },
    presentation: {
      label: 'Presentation',
      icon: PresentationIcon,
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-700'
    },
    url: {
      label: 'External link',
      icon: ExternalLinkIcon,
      bgColor: 'bg-green-100',
      textColor: 'text-green-700'
    }
  }[type]

  const Icon = config.icon

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      <Icon size={12} />
      {config.label}
    </span>
  )
}

const PartnersResourcesPage = ({ resources }: { resources: Resource[] }) => {
  const [activeCategory, setActiveCategory] = useState<ResourceCategory>('All')

  const filteredResources = useMemo(() => {
    if (activeCategory === 'All') return resources
    return resources.filter(resource => resource.category.toLowerCase() === activeCategory.toLowerCase())
  }, [resources, activeCategory])

  return (
    <div className='w-full h-full flex flex-col'>
      <div className='flex flex-row items-start justify-between flex-wrap gap-3 mb-6'>
        <h1 className='heading-lg'>Resources</h1>
      </div>
      <div className='max-w-[680px] mb-4'>
        <h4 className='font-inter text-3 text-gray-500 font-normal leading-[20px]'>
          Explore resources to help you support your clients on Thatch and learn more about ICHRA.
        </h4>
      </div>
      <CategoryFilter activeCategory={activeCategory} setActiveCategory={setActiveCategory} />

      <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4'>
        {filteredResources.map((resource) => (
          <MarketplaceCard
            key={resource.id}
            href={resource.url}
            target='_blank'
            onClick={() => {
              window.segment?.track('[TfB] Resource clicked', {
                resource_id: resource.id,
                resource_name: resource.name,
                resource_category: resource.category,
                resource_url: resource.url
              })
            }}
          >
            <MarketplaceCard.Thumbnail
              src={resource.image_url}
            />
            <MarketplaceCard.Body>
              <MarketplaceCard.Title>{resource.name}</MarketplaceCard.Title>
              <div className='flex flex-col justify-between flex-grow min-h-[80px]'>
                <MarketplaceCard.Description>
                  {resource.description}
                </MarketplaceCard.Description>
                <div className='mt-4 pt-2'>
                  <ResourceTypeTag type={resource.type} />
                </div>
              </div>
            </MarketplaceCard.Body>
          </MarketplaceCard>
        ))}
      </ul>
    </div>
  )
}

const CategoryFilter = ({
  activeCategory,
  setActiveCategory
}: {
  activeCategory: ResourceCategory,
  setActiveCategory: (category: ResourceCategory) => void
}) => {
  const handleCategoryClick = (category: ResourceCategory) => {
    window.segment?.track('[TfB] Resource category selected', { category })
    setActiveCategory(category)
  }

  return (
    <div className='flex flex-wrap items-center gap-2 mb-6'>
      {categories.map(({ category, displayName, Icon }) => {
        const isActive = activeCategory === category
        return (
          <button
            key={category}
            type='button'
            onClick={() => handleCategoryClick(category)}
            className={`${baseButtonStyles} ${isActive ? activeStyles : inactiveStyles}`}
          >
            <Icon size={iconSize} className={`${isActive ? 'text-thatch-600' : 'text-gray-500'}`} />
            <span>{displayName}</span>
          </button>
        )
      })}
    </div>
  )
}

const categories: { category: ResourceCategory, Icon: React.ElementType, displayName: string }[] = [
  { category: 'All', Icon: ShapesIcon, displayName: 'All' },
  { category: 'Assets', Icon: PresentationIcon, displayName: 'For your clients' },
  { category: 'Education', Icon: HandshakeIcon, displayName: 'For brokers' },
]

const baseButtonStyles = 'flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full text-sm px-[14px] h-10 transition focus:outline-none focus-visible:outline-thatch-500 focus-visible:outline-offset-[-2px] h-8'
const activeStyles = 'bg-thatch-100 text-thatch-600 border border-thatch-500 font-semibold'
const inactiveStyles = 'bg-white border border-gray-200 text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-100 font-normal'

const iconSize = 16

export default PartnersResourcesPage
